import {Component, ViewChild, Input} from '@angular/core';
import {Router, ActivatedRoute} from '@angular/router';
import {UtilsService} from '../services/utils';
import {SecretService} from '../services/secret';
import {OpenAPIService} from '../services/openapi';
import {TrackerService} from '../services/tracker';
import {GitHubService} from '../services/github';
import {RoutesService} from '../services/routes';
import {MenuService} from '../services/menu';

declare let window:any;

const PARAM_DEPTH_FOR_EDITOR = 7;

const NEW_WORKFLOW_DESCRIPTION = `
Use the text editor to write a description of what your workflow
will cover. You can use
[markdown](https://github.com/adam-p/markdown-here/wiki/Markdown-Cheatsheet)

To add the first step, click the  <i class="fa fa-plus"></i>  icon in the progress bar above.

If you want to see how your workflow looks, open the menu
<code><i class="fa fa-caret-down"></i></code>
above the progress bar (next to the title) and click \`Preview\`.
To come back to the editor, click \`Edit\` in the same menu.

Once you're done, open the menu
above the progress bar and click \`Save\`. We'll open a pull request in
[${window.config.github.repo}](https://github.com/${window.config.github.repo})
which adds the new workflow. It will go live shortly after being merged.

Once it's saved to GitHub, you can always come back to your workflow by clicking the
\`Load\` button in the same menu. You can get the URL for your saved workflow
by going to the pull request you generated, choosing the recipe's \`readme.md\`
file, and clicking 'Raw'.
`.trim();

@Component({
    selector: 'recipe',
    templateUrl: '../views/recipe/main.pug',
    styles: [`
      .remove-param-button {
        cursor: pointer;
      }
    `],
})
export class RecipeComponent {
  @Input() name:string;
  origRecipe:any;
  recipe:any;
  recipes:any[] = window.config.workflows;
  allAPICalls:any[];
  previewURL:string;
  activeStep:any;
  stepStates:any[];
  flatParameters:any[];
  templateInput:any;
  menuExpanded:boolean;
  defaultModel:any = {};
  marked=window.marked;
  @ViewChild('sampleCode') sampleCodeComponent;
  @ViewChild('apiResponse') apiResponseComponent;
  @ViewChild('demo') demoComponent;

  editing:boolean;
  edited:boolean;
  assetsToUpload:any[] = [];
  saving:boolean;
  saveSuccess:boolean;
  error:string;
  executing:boolean;
  feedback:any = {};
  feedbackAlert:any = {};
  repoToSearch:string;
  previewURLToLoad:string;

  subscriptions:any[] = [];

  constructor(
        private route: ActivatedRoute,
        public router: Router,
        private routes: RoutesService,
        public utils: UtilsService,
        private tracker: TrackerService,
        private secrets: SecretService,
        private menu: MenuService,
        public openapi: OpenAPIService,
        public github: GitHubService) {
    window.recipeComponent = this;
    this.repoToSearch = this.utils.config.github.repo;
    if (this.github.user) {
      this.repoToSearch = this.github.user.login + '/' + this.utils.config.github.repo.split('/')[1];
    }
  }

  firstIncompleteStep() {
    for (let i = 0; i < this.stepStates.length; ++i) {
      if (!this.stepStates[i].completed) return i;
    }
    return -1;
  }

  ngOnChanges() {
    if (this.name && this.origRecipe !== this.recipes[this.name]) {
      this.origRecipe = this.recipes[this.name];
      this.initializeRecipe();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  ngOnInit() {
    this.subscriptions.push(this.route.params.subscribe(params => {
      if (params['recipe'] && params['recipe'] !== this.name) {
        window.RECIPE_NAME = this.name = params['recipe'];
        this.origRecipe = this.recipes[this.name];
        this.initializeRecipe();
      } else if (params['previewURL'] && this.previewURL !== params['previewURL']) {
        this.initializeRecipeFromURL(params['previewURL']);
        window.jQuery('#LoadRecipeModal').modal('hide');
        // FIXME: changing route causes bootstrap to lose track of the modal
        window.jQuery('body').removeClass('modal-open');
        window.jQuery('.modal-backdrop').remove();
      } else if (this.route.snapshot.url[0].path === 'new-workflow' && !this.origRecipe) {
        this.origRecipe = {
          title: 'New Workflow',
          description: NEW_WORKFLOW_DESCRIPTION,
          steps: [],
        }
        this.initializeRecipe();
        this.startEditing();
      }

      if (params['step'] === 'intro' || params['step'] === 'finish') {
        this.activeStep = params['step'];
      } else if (params['step'] !== undefined) {
        let step = +params['step'] - 1;
        this.activeStep = (this.editing ? this.origRecipe : this.recipe).steps[step];
        if (!this.activeStep) return this.navigateToStep('intro');
      } else {
        this.activeStep = 'intro';
      }
      if (this.recipe && this.stepStates) {
        this.setParameterValuesForActiveStep();
        this.setTemplateInput();
      }
      this.error = null;
      this.menuExpanded = false;
    }));
  }

  startEditing() {
    this.navigateToStep('intro')
    this.setFlatParameters();
    this.editing = this.edited = true;
  }

  stopEditing() {
    this.navigateToStep('intro')
    this.initializeRecipe();
    this.editing = false;
  }

  executeStep() {
    let curOp = this.activeStep.apiCall ? this.activeStep.apiCall.operation : null;
    if (curOp && !this.secrets.isAuthenticated(curOp)) return this.secrets.startAuthentication();
    this.error = null;
    let state = this.stepStates[this.recipe.steps.indexOf(this.activeStep)];
    if (!this.activeStep.apiCall) {
      if (this.demoComponent) {
        this.demoComponent.refreshDemo();
        this.scrollToResponse();
      }
      state.completed = true;
      return;
    }
    this.executing = true;
    state.response = state.statusCode = state.headers = undefined;
    this.openapi.request({
      path:    this.activeStep.apiCall.path,
      method:  this.activeStep.apiCall.method,
      answers: state.answers,
      embed: true,
    })
    .then((data, textStatus, xhr) => {
      state.response = data;
      state.statusCode = xhr.status;
      state.headers = xhr.getAllResponseHeaders().split('\r\n');
      if (window.checkResponse) {
        state.completed = window.checkResponse(data, xhr.status).type === 'success';
      } else {
        state.completed = true;
      }
    })
    .fail(xhr => {
      state.response = xhr.responseText;
      state.statusCode = xhr.status;
      state.headers = xhr.getAllResponseHeaders().split('\r\n');
    })
    .always(_ => {
      this.executing = false;
      this.setTemplateInput();
      this.tracker.track('execute_step', {
        workflow: this.name,
        step: this.recipe.steps.indexOf(this.activeStep),
        success: state.completed,
      })
      setTimeout(() => { // FIXME: run after response is rendered
        this.scrollToResponse();
      }, 100)
    })
  }

  scrollToResponse() {
    let response = window.jQuery('.step').not('[hidden]').find('.response');
    this.utils.scrollToSelection(response);
  }

  onInputChange() {
    this.setTemplateInput();
    if (this.sampleCodeComponent) this.sampleCodeComponent.refreshSampleCode();
  }

  onNavigate(dir) {
    let targetIdx = this.recipe.steps.indexOf(this.activeStep) + dir;
    if (targetIdx < 0) {
      this.navigateToStep('intro');
    } else if (targetIdx >= this.recipe.steps.length) {
      this.navigateToStep('finish');
    } else {
      this.navigateToStep(targetIdx);
    }
  }

  setParameterValuesForActiveStep () {
    if (this.editing || !this.activeStep || typeof this.activeStep === 'string') return;
    let activeState = this.stepStates[this.recipe.steps.indexOf(this.activeStep)];
    if (!this.activeStep.apiCall && !this.activeStep.demoHTML) {
      activeState.completed = true;
    }
    if (!this.activeStep.parameters) return;
    this.activeStep.parameters
      .filter(p => p.dynamicValue && p.dynamicValue.fromStep !== undefined)
      .forEach(param => {
        let dv = param.dynamicValue;
        var fromState = this.stepStates[dv.fromStep];
        if (dv.answer) {
          if (dv.answer.indexOf('body.') === 0) {
            let body = JSON.parse(fromState.answers.body || '{}');
            activeState.answers[param.name] = this.utils.followPath({body}, dv.answer);
          } else {
            activeState.answers[param.name] = fromState.answers[dv.answer];
          }
        } else if (dv.value) {
          if (!fromState.response) {
            this.error = "Please complete step " + (dv.fromStep + 1) + " first.";
          } else {
            activeState.answers[param.name] = this.utils.followPath(fromState.response, dv.value);
          }
        }
      });
    this.activeStep.parameters
      .filter(p => p.schema)
      .forEach(p => this.setSchemaValuesForActiveStep(p.schema))
  }

  setSchemaValuesForActiveStep(schema) {
    if (schema.dynamicValue) {
      let fromState = this.stepStates[schema.dynamicValue.fromStep];
      if (schema.dynamicValue.answer) {
        if (schema.dynamicValue.answer.indexOf('body.') === 0 && fromState.answers.body) {
          let body = JSON.parse(fromState.answers.body);
          schema.consoleDefault = this.utils.followPath({body}, schema.dynamicValue.answer);
        } else {
          schema.consoleDefault = fromState.answers[schema.dynamicValue.answer];
        }
      } else if (schema.dynamicValue.value) {
        if (!fromState.response) {
          this.error = "Please complete step " + (schema.dynamicValue.fromStep + 1) + " first.";
        } else {
          schema.consoleDefault = this.utils.followPath(fromState.response, schema.dynamicValue.value);
        }
      }
    }
    for (let key in schema.properties || {}) {
      this.setSchemaValuesForActiveStep(schema.properties[key]);
    }
  }

  initializeRecipe() {
    if (!this.openapi.parsed) {
      console.log('OpenAPI not loaded yet, waiting...');
      return setTimeout(() => this.initializeRecipe(), 100);
    }
    this.recipe = JSON.parse(JSON.stringify(this.origRecipe));
    this.stepStates = this.recipe.steps.map(s => ({answers: {}}))
    this.recipe.steps
      .filter(s => s.apiCall)
      .forEach(step => {
        let ignore = step.ignoreParameters || step.apiCall.ignoreParameters || [];
        if (ignore.length) step.ignoreParameters = ignore;
        let disableSetupCode = step.apiCall.disableSetupCode;
        step.apiCall = this.openapi.resolveAPICall(step.apiCall);
        if (disableSetupCode) step.apiCall.disableSetupCode = true;
        let usingDefaultParameters = !step.parameters;
        if (usingDefaultParameters) {
          step.parameters = step.apiCall.operation.parameters
                .filter(p => ignore.indexOf(p.name) === -1)
                .filter(p => !p.global);
        }
        function findGroup(name, groups=null) {
          groups = groups || step.apiCall.operation.parameterGroups;
          let group = groups.filter(g => g.name === name)[0];
          if (group) return group;
          let subs = groups.filter(g => g.subGroups).map(g => findGroup(name, g.subGroups));
          return subs[0];
        };
        (step.parameters || []).forEach(param => {
          this.openapi.replaceXValues(param);
          if (param.group) {
            param.group = findGroup(param.group);
            return;
          }
          let opParam = (step.apiCall.operation.parameters || []).filter(p => p.name === param.name)[0];
          if (!opParam) return;
          Object.keys(opParam)
            .filter(k => k.indexOf('x-') !== 0)
            .filter(k => param[k] === undefined)
            .forEach(k => param[k] = opParam[k]);
          if (!usingDefaultParameters && param.schema) {
            this.openapi.fillSchema(param.schema, opParam.schema);
          }
        })
      })
    this.setAllAPICalls();
    this.setParameterValuesForActiveStep();
    this.setTemplateInput();
  }

  initializeRecipeFromURL(url) {
    this.previewURL = url;
    window.jQuery.get(url)
      .done(data => {
        this.origRecipe = this.utils.markdownToRecipe(data);
        this.name = url.match(/.*\/([^\/]+)\/readme.md$/)[1];
        this.activeStep = 'intro';
        this.initializeRecipe();
      })
      .fail(xhr => {
        this.error = xhr.responseText;
      })
  }

  hasVisibleParameters(step) {
    return step.parameters && step.parameters.filter(p => !p.hidden).length;
  }

  linkToStep(step) {
    if (typeof step === 'number') ++step;
    let params:any = {step};
    if (this.previewURL) params.previewURL = this.previewURL;
    return ['.', params];
  }

  navigateToStep(step) {
    this.router.navigate(this.linkToStep(step), {relativeTo: this.route});
  }

  getAPICallTitle(opts) {
    if (!opts.method || !opts.path) return '';
    let op = this.openapi.parsed.paths[opts.path][opts.method];
    if (!op || !op.operationId) return opts.method.toUpperCase() + ' ' + opts.path;
    return op.operationId;
  }

  setAllAPICalls() {
    this.allAPICalls = this.recipe.steps.filter(s => s.apiCall).map(s => s.apiCall)
    this.allAPICalls = this.allAPICalls.filter((apiCall, idx) => {
      for (let i = 0; i < idx; ++i) {
        if (this.allAPICalls[i].method === apiCall.method && this.allAPICalls[i].path === apiCall.path) {
          return false;
        }
      }
      return true;
    });
  }

  setTemplateInput() {
    if (this.editing || !this.activeStep || typeof this.activeStep === 'string') return;
    let curStepState = this.stepStates[this.recipe.steps.indexOf(this.activeStep)];
    this.templateInput = {
      answers: curStepState.answers,
      result: curStepState.response,
      results: this.stepStates.map(state => state.response),
    }
  }

  sendFeedback () {
    this.feedbackAlert = {};
    this.tracker.track('feedback', {
      recipe: this.name,
      feedback: this.feedback.text,
      rating: this.feedback.rating,
    });
    this.feedbackAlert.success = 'Thanks! Your feedback has been recorded.';
  }
  rateRecipe (rating) {
    this.feedback.rating = rating;
    this.tracker.track('rate_recipe', {
      recipe: this.name,
      rating: rating,
    })
  }

  /* Editing Functions */
  addStep() {
    let newStep:any = {
      title: 'New Step',
    };
    if (!this.origRecipe.steps.length) {
      newStep.description = `
## Adding your first step
Each step of the recipe should describe a single task, usually an API call.
Use this area to tell the user what's happening, and how this step relates to the
workflow as a whole.

Below, you can select an API call that the user will make in this step.
By default, all of it's parameters will be available to the user, but you can also
add them manually.

In later steps, you can use the 'Dynamic Value' section under each parameter
if you want to auto-fill the input using the users's answer from a previous
step, or a field in the API response (JSON only).

If you're making an API call, we'll auto-generate sample code in a few different
programming languages. You can also manually add code using the 'Sample Code' box below.

In manually entered sample code, you can use EJS to create templates that utilize
the user's answers in the Parameters box, or fields from the API response. For example:

\`\`\`
var myName = "<%- answers.name %>";
\`\`\`

At the bottom of the page, you can also include an HTML demo which will be displayed
in an iframe. This too can be an EJS template that utilizes the user's inputs or
API response. For example:

\`\`\`
<h2>Search results</h2>
<% if (response) { -%>
<%   for (var i = 0; i < response.length; ++i) { -%>
  <h4><%- response[i].title %></h4>
  <p><%- response[i].description %></p>
<%   } -%>
<% } -%>
\`\`\`
`.trim();
    }
    this.origRecipe.steps.push(newStep);
    this.stepStates.push({answers: {}});
    this.navigateToStep(this.origRecipe.steps.length - 1);
  }

  setFlatParameters() {
    this.flatParameters = []
    this.origRecipe.steps.forEach((step, stepIdx) => {
      let op = step.apiCall ? this.openapi.parsed.paths[step.apiCall.path][step.apiCall.method] : null;
      let params = step.parameters.map(p => {
        return {
          parameter: p,
          name: p.name,
          details: op ? op.parameters.find(opP => p.name === opP.name) : p,
          schema: p.schema,
        }
      });
      let bodyParam = params.find(p => p.details.schema);
      if (!bodyParam) return params;
      let expandForSchema = (base, smallSchema, bigSchema, depth) => {
        if (!bigSchema || !smallSchema || !smallSchema.properties) return;
        if (bigSchema.$ref) bigSchema = this.openapi.resolveReference(bigSchema.$ref);
        Object.keys(smallSchema.properties).forEach(p => {
          let param = smallSchema.properties[p];
          let name = base + '.' + p;
          let details = this.openapi.findProperty(bigSchema, p);
          if (!details) return;
          if (details.$ref) details = this.openapi.resolveReference(details.$ref);
          details.name = name;
          params.push({parameter: param, schema: param, details, name});
          if (depth > 0) {
            expandForSchema(name, smallSchema.properties[p], bigSchema.properties[p], depth - 1);
          }
        });
      }
      expandForSchema('body', bodyParam.parameter.schema, bodyParam.details.schema, PARAM_DEPTH_FOR_EDITOR);
      this.flatParameters.push(params);
    });
  }

  getAddableParameters(step) {
    if (!step.apiCall) return [];
    let op = this.openapi.parsed.paths[step.apiCall.path][step.apiCall.method];
    let params = op.parameters.map(p => p.name);
    let body = op.parameters.find(p => p.in === 'body' && !p['x-global']);
    if (!body || !body.schema) return params;
    let expandForSchema = (base, schema, depth) => {
      if (schema.$ref) schema = this.openapi.resolveReference(schema.$ref);
      if (!schema.properties) return;
      if (depth > 0) {
        let subs = [];
        [schema.allOf, schema.oneOf, schema.anyOf].filter(s => s).forEach(s => subs = subs.concat(s));
        subs.forEach(subschema => {
          expandForSchema(base, subschema, depth - 1);
        })
      }
      Object.keys(schema.properties).forEach(p => {
        let pSchema = schema.properties[p];
        let newKey = base + '.' + p;
        let existing = params.find(p => p === newKey);
        if (!pSchema.readOnly && !existing) {
          params.push(newKey);
        }
        if (depth > 0) {
          expandForSchema(newKey, schema.properties[p], depth - 1);
        }
      })
    }
    expandForSchema('body', body.schema, PARAM_DEPTH_FOR_EDITOR);
    return params;
  }

  addParameterToStep(name, step) {
    step.parameters = step.parameters || [];
    if (name.indexOf('body.') === 0 || name === 'body') {
      let bodyParam = step.parameters.find(p => p.schema);
      if (!bodyParam) {
        bodyParam = {name: 'body', schema: {type: 'object', properties: {}}};
        step.parameters.push(bodyParam);
      }
      let parts = name.split('.');
      parts.shift();
      let schema = bodyParam.schema;
      for (let part of parts) {
        schema.type = schema.type || 'object';
        schema.properties = schema.properties || {}
        schema = schema.properties[part] = schema.properties[part] || {}
      }
    } else {
      step.parameters.push({name});
    }
    this.setFlatParameters();
  }

  removeParameterFromStep(name, step) {
    if (name.indexOf('body.') === 0) {
      let bodyParam = step.parameters.find(p => p.schema);
      let parts = name.split('.');
      parts.shift();
      let schema = bodyParam.schema;
      for (let i = 0; i < parts.length - 1; ++i) {
        schema = schema.properties[parts[i]];
      }
      delete schema.properties[parts.pop()];
      if (Object.keys(schema.properties).length === 0) {
        delete schema.properties;
      }
    } else {
      step.parameters = step.parameters.filter(s => s.name !== name);
    }
    this.setFlatParameters();
  }

  addAssetToUpload(evt) {
    for (let i = 0; i < evt.target.files.length; ++i) {
      let file = evt.target.files[i];
      let reader = new FileReader();
      reader.onload = () => {
        let data = reader.result;
        const TYPE_END = 'base64,';
        let typeLoc = data.indexOf('base64,');
        if (typeLoc === -1) return;
        data = data.substring(typeLoc + TYPE_END.length);
        this.assetsToUpload.push({filename: file.name, data});
      }
      reader.readAsDataURL(file);
    }
  }

  save() {
    if (!this.github.access_token) {
      this.github.authorize((err) => {
        if (!err) this.save()
      });
      return;
    }

    this.saving = true;
    this.saveSuccess = false;
    if (!this.name) this.name = this.origRecipe.title.replace(/\W/g, '_').replace(/_+/g, '_').toLowerCase();
    let repoPath = '/repos/' + window.config.github.repo;
    let filePath = '/contents/' + window.config.workflowDirectory + '/' + this.name + '/readme.md';
    let fork = null;
    let recipeMarkdown = this.utils.recipeToMarkdown(this.origRecipe);
    let promise = this.github.post(repoPath + '/forks')
      .then(repo => {
        fork = repo;
        return this.github.get('/repos/' + fork.full_name + filePath).catch(e => {})
      })
      .then(file => {
        let commit = {
          message: "Update workflow: " + this.name,
          content: btoa(window.unescape(encodeURIComponent(recipeMarkdown))),
          sha: file ? file.sha : undefined,
        }
        return this.github.put('/repos/' + fork.full_name + filePath, commit)
      });
    this.assetsToUpload.forEach(asset => {
      let assetPath = '/contents/assets/' + asset.filename;
      promise = promise.then(() => {
        return this.github.get('/repos/' + fork.full_name + assetPath).catch(e => {})
      }).then(file => {
        let commit = {
          message: "Update workflow: " + this.name,
          content: asset.data,
          sha: file ? file.sha : undefined,
        }
        return this.github.put('/repos/' + fork.full_name + assetPath, commit)
      })
    })
    promise = promise.then(() => {
        let pr = {
          title: 'Update workflows',
          base: 'master',
          head: fork.owner.login + ':master',
        }
        return this.github.post(repoPath + '/pulls', pr).catch(e => {});
      })
      .then(() => {
        this.saveSuccess = true;
      })
      .catch(e => {
        this.error = e;
      })
      .then(() => {
        this.saving = false;
      });
  }
}



// WEBPACK FOOTER //
// ./src/app/components/recipe.ts